'use client'

import { useRef } from 'react'

import type { TDispatcher } from '../../../../../types/shipment'
import { AssetSelectionStep } from './components/trailer-truck-selection/asset-selection-step'
import { ELDAppList } from './components/trailer-truck-selection/eld-app-list'
import { ModalFooter } from './components/trailer-truck-selection/modal-footer'
import { ModalHeader } from './components/trailer-truck-selection/modal-header'
import { WizardStepIndicator } from './components/trailer-truck-selection/wizard-step-indicator'
import { useELDAssetSelection } from './hooks/use-eld-asset-selection'

interface ITruckTrailerSelectionPopupProps {
  currentTrailerNumber?: string | null
  currentTruckNumber?: string | null
  dispatchers?: TDispatcher[]
  isOpen: boolean
  onClose: () => void
  onTrailerChange: (trailerNumber: string | null) => void
  onTruckChange: (truckNumber: string | null) => void
  shipmentID: string
  shipmentOwnerWorkspaceID: string
}

/**
 * Two-step wizard modal for selecting truck or trailer from ELD apps
 * Step 1: Select ELD Provider
 * Step 2: Select Asset (with asset type tabs, search, and location preview)
 * Includes inline confirmation for removal and replacement of existing values
 */
export const TruckTrailerSelectionPopup = ({
  currentTrailerNumber,
  currentTruckNumber,
  dispatchers,
  isOpen,
  onClose,
  onTrailerChange,
  onTruckChange,
  shipmentID,
  shipmentOwnerWorkspaceID
}: ITruckTrailerSelectionPopupProps) => {
  const prevIsOpenRef = useRef(isOpen)

  const {
    activeAssetType,
    assets,
    confirmingAssetType,
    currentValue,
    eldApps,
    handleActiveAssetTypeChange,
    handleAssetSelect,
    handleBack,
    handleCancelConfirmation,
    handleConfirmAsset,
    handleConfirmRemove,
    handleConfirmReplace,
    handleDualCancelConfirmation,
    handleDualConfirmRemove,
    handleDualConfirmReplace,
    handleELDAppSelect,
    handleManualSubmit,
    handleRemoveClick,
    handleTrailerRemoveClick,
    handleTrailerSubmit,
    handleTruckRemoveClick,
    handleTruckSubmit,
    isLoadingUpdate,
    loadingAssets,
    loadingELDApps,
    manualInputValue,
    pendingAction,
    refetchELDApps,
    resetState,
    selectingAsset,
    selectedELDApp,
    selectedItem,
    setManualInputValue,
    setTrailerInputValue,
    setTruckInputValue,
    showRemoveConfirmation,
    showReplaceConfirmation,
    step,
    trailerInputValue,
    truckInputValue
  } = useELDAssetSelection({
    currentTrailerNumber,
    currentTruckNumber,
    dispatchers,
    isOpen,
    onTrailerChange,
    onTruckChange,
    shipmentID,
    shipmentOwnerWorkspaceID
  })

  // Reset state when modal closes
  if (prevIsOpenRef.current && !isOpen) {
    resetState()
  }
  prevIsOpenRef.current = isOpen

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSuccessfulUpdate = () => {
    handleClose()
  }

  const handleConfirmButtonClick = () => {
    handleConfirmAsset(handleSuccessfulUpdate)
  }

  const handleManualSubmitWithClose = () => {
    handleManualSubmit(handleSuccessfulUpdate)
  }

  if (!isOpen) return null

  const isConfirmDisabled = !selectedItem || isLoadingUpdate || showRemoveConfirmation || showReplaceConfirmation

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-zinc-950/80 backdrop-blur-sm">
      <div className="bg-background w-full max-w-md rounded-lg border border-border shadow-lg overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <ModalHeader title="Select Asset" subtitle="Connect your ELD to track location" onClose={handleClose} />

        {/* Step Indicator */}
        <WizardStepIndicator currentStep={step} />

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {step === 1 ? (
            /* Step 1: ELD Provider Selection */
            <div className="space-y-2">
              <ELDAppList
                apps={eldApps}
                confirmingAssetType={confirmingAssetType}
                currentTrailerNumber={currentTrailerNumber}
                currentTruckNumber={currentTruckNumber}
                isLoadingUpdate={isLoadingUpdate}
                loading={loadingELDApps}
                onAppSelect={handleELDAppSelect}
                onCancelConfirmation={handleDualCancelConfirmation}
                onConfirmRemove={() => handleDualConfirmRemove(handleSuccessfulUpdate)}
                onConfirmReplace={() => handleDualConfirmReplace(handleSuccessfulUpdate)}
                onTrailerInputChange={setTrailerInputValue}
                onTrailerRemoveClick={handleTrailerRemoveClick}
                onTrailerSubmit={() => handleTrailerSubmit(handleSuccessfulUpdate)}
                onTruckInputChange={setTruckInputValue}
                onTruckRemoveClick={handleTruckRemoveClick}
                onTruckSubmit={() => handleTruckSubmit(handleSuccessfulUpdate)}
                pendingAction={pendingAction}
                refetchELDApps={refetchELDApps}
                showRemoveConfirmation={showRemoveConfirmation}
                showReplaceConfirmation={showReplaceConfirmation}
                trailerInputValue={trailerInputValue}
                truckInputValue={truckInputValue}
              />
            </div>
          ) : (
            /* Step 2: Asset Selection */
            <AssetSelectionStep
              actions={{
                onActiveAssetTypeChange: handleActiveAssetTypeChange,
                onAssetSelect: handleAssetSelect,
                onBack: handleBack,
                onCancelConfirmation: handleCancelConfirmation,
                onConfirmRemove: () => handleConfirmRemove(handleSuccessfulUpdate),
                onConfirmReplace: () => handleConfirmReplace(handleSuccessfulUpdate),
                onManualInputChange: setManualInputValue,
                onManualSubmit: handleManualSubmitWithClose,
                onRemoveClick: handleRemoveClick
              }}
              isLoadingUpdate={isLoadingUpdate}
              state={{
                activeAssetType,
                assets,
                currentTrailerNumber,
                currentTruckNumber,
                currentValue,
                loadingAssets,
                manualInputValue,
                pendingAction,
                selectingAsset,
                selectedELDApp,
                selectedItem,
                showRemoveConfirmation,
                showReplaceConfirmation
              }}
            />
          )}
        </div>

        {/* Footer */}
        <ModalFooter
          step={step}
          selectedItem={selectedItem}
          isLoadingUpdate={isLoadingUpdate}
          isConfirmDisabled={isConfirmDisabled}
          onBackClick={handleBack}
          onCancelClick={handleClose}
          onConfirmClick={handleConfirmButtonClick}
        />
      </div>
    </div>
  )
}
